import { takeEvery, put, call, select } from 'redux-saga/effects'

import {
  checkAuthInfoSuccess,
  checkAuthInfoFailed,
  getCustomersChartSuccess,
  getCustomersChartFailed,
  getInvoiceChartSuccess,
  getInvoiceChartFailed,
  getUserTaxInfoSuccess,
  getCompanyUpdatesSuccess,
  onRenewToken,
  setClient,
  sendReferralSuccess,
  sendReferralFailed,
  emailReferredOrRegistered,
  getReferralDiscountByEmailSuccess,
  getReferralDiscountByEmailFailed,
  setReadCompanyUpdateSuccess,
  setReadCompanyUpdateFailed,
  getUserCompanyUpdateInfoSuccess,
  getUserCompanyUpdateInfoFailed,
  getUserCompanyUpdateInfo,
  getUserLifeTimeInfoSuccess,
  getUserLifeTimeInfoFailed,
  updateCelebrationStatusSuccess,
  updateCelebrationStatusFailed,
  getInvoiceAndSalaryDetailMonthlySuccess,
  getInvoiceAndSalaryDetailMonthlyFailed
} from '../actions'
import {
  CHECK_AUTH_INFO,
  GET_CUSTOMERS_CHART,
  GET_INVOICE_CHART,
  GET_USER_TAX_INFO,
  GET_COMPANY_UPDATES,
  API_SERVER,
  KVT_IDENTITY_SERVER,
  GET_RENEW_TOKEN,
  TOKEN_VALIDATION,
  SEND_REFERRAL,
  GET_REFERRAL_DISCOUNT_BY_EMAIL,
  SET_READ_COMPANY_UPDATE,
  GET_USER_COMPANY_UPDATE_INFO,
  GET_USER_LIFETIME_INFO,
  UPDATE_CELEBRATE_STATUS,
  GET_INVOICE_AND_SALARY_DETAIL_MONTHLY
} from '../constants'
import {
  apiPost,
  apiManualPost,
  apiManualRequest,
  registerPost,
  apiSessionStorageRequest
} from '../utils/request'
import store from '../store'
import { mergePaidInvoices, propertyArray } from '../utils/invoice.utils'
import {
  chartDataFormat,
  calcPercent,
  labelInvoiceMonthlyArray,
  labelInvoiceArray
} from '../utils/dashboard.utils'
import { getUserType, getUuid } from '../selectors/client.selector'
import { getYear } from '../selectors/dashboard.selector'

/**
 * @author Pragya Gupta
 */

function* checkAuthInfoSaga() {
  try {
    const url = `${API_SERVER}/user-check`
    const profile = store.getState().oidc.user.profile
    const body = JSON.stringify({
      first_name: profile.given_name,
      last_name: profile.family_name,
      email: profile.email
    })
    const result = yield call(apiPost, url, body)
    yield put(checkAuthInfoSuccess(result.data))
  } catch (e) {
    yield put(checkAuthInfoFailed(e))
  }
}

function* getInvoiceChartSaga({ data }) {
  const selectedUser = data

  //console.log('---------Inside getInvoiceChart, selectedUser-----------', selectedUser)
  try {
    const url = `${API_SERVER}/GetInfoInvoiceChart`
    const uuid = store.getState().client.user.data[2]
    const userType = store.getState().client.user.data[5]

    const body = JSON.stringify({
      uuid: userType == 2 ? selectedUser.uuid : uuid
    })
    const result = yield call(apiManualPost, url, body)
    const resultParsed = JSON.parse(result.data)
    const mergePaidPaidMPInvoices = mergePaidInvoices(resultParsed)
    const labels = propertyArray(mergePaidPaidMPInvoices, 'status')
    const convLabels = labelInvoiceArray(labels)
    const data = propertyArray(mergePaidPaidMPInvoices, 'invoicecount')
    const percentData = calcPercent(data)
    const bgColor = [
      'rgba(68, 192, 204, 0.8)',
      'rgba(11, 112, 112, 0.8)',
      'rgba(54, 213, 217, 0.8)',
      'rgba(246, 113, 49, 1)'
    ]

    const chartData = chartDataFormat(convLabels, percentData, bgColor)
    yield put(getInvoiceChartSuccess(chartData))
  } catch (e) {
    yield put(getInvoiceChartFailed(e))
  }
}

function* getCustomersChartSaga({ data }) {
  const selectedUser = data
  try {
    const url = `${API_SERVER}/GetInfoCustomersChart`
    const uuid = store.getState().client.user.data[2]
    const userType = store.getState().client.user.data[5]

    const body = JSON.stringify({
      uuid: userType == 2 ? selectedUser.uuid : uuid
    })
    const result = yield call(apiManualPost, url, body)
    const resultParsed = JSON.parse(result.data)
    const labels = propertyArray(resultParsed, 'company_name')
    const data = propertyArray(resultParsed, 'sum')
    const percentData = calcPercent(data)
    const bgColor = [
      'rgba(68, 192, 204, 0.8)',
      'rgba(11, 112, 112, 0.8)',
      'rgba(54, 213, 217, 0.8)',
      'rgba(246, 113, 49, 1)',
      'rgba(35, 153, 223, 0.8)'
    ]

    const chartData = chartDataFormat(labels, percentData, bgColor)
    yield put(getCustomersChartSuccess(chartData))
  } catch (e) {
    yield put(getCustomersChartFailed(e))
  }
}

function* getInvoiceAndSalaryDetailMonthlySaga({ data }) {
  const selectedUser = data
  try {
    const url = `${API_SERVER}/GetSumInvoiceAndSalaryByMonthly`
    const uuid = yield select(getUuid)
    const userType = yield select(getUserType)
    const year = yield select(getYear)
    const body = JSON.stringify({
      uuid: userType == 2 ? selectedUser.uuid : uuid,
      year: year
    })
    const result = yield call(apiManualPost, url, body)
    const resultParsed = JSON.parse(result.data)
    const labels = propertyArray(resultParsed, 'Month')
    const convLabels = labelInvoiceMonthlyArray(labels)

    // Monthly invoicing
    const monthly_invoicing_data = propertyArray(resultParsed, 'SumwithTax')
    const bgColor_monthly_invoicing = 'rgba(246, 113, 49, 1)'
    const monthly_invoicing_chart_data = chartDataFormat(
      convLabels,
      monthly_invoicing_data,
      bgColor_monthly_invoicing,
      'monthly_invoicing'
    )

    // Gross salary
    const gross_salary_data = propertyArray(resultParsed, 'gross_salary')
    const bgColor = '#0b7070'
    const gross_salary_chart_data = chartDataFormat(
      convLabels,
      gross_salary_data,
      bgColor,
      'gross_salary'
    )

    // Raised salary
    const raise_salary_data = propertyArray(resultParsed, 'raised_salary')
    const bgColor_raised_salary = '#44c0cc'
    const raised_salary_chart_data = chartDataFormat(
      convLabels,
      raise_salary_data,
      bgColor_raised_salary,
      'raised_salary'
    )

    yield put(
      getInvoiceAndSalaryDetailMonthlySuccess(
        monthly_invoicing_chart_data,
        gross_salary_chart_data,
        raised_salary_chart_data
      )
    )
  } catch (error) {
    yield put(getInvoiceAndSalaryDetailMonthlyFailed(error))
  }
}

function* getUserTaxInfoSaga({ data }) {
  const selectedUser = data
  try {
    const url = `${API_SERVER}/GetUserTaxInfo`
    const uuid = store.getState().client.user.data[2]
    const userType = store.getState().client.user.data[5]
    const body = JSON.stringify({
      uuid: userType == 2 ? selectedUser.uuid : uuid
    })
    const result = yield call(apiManualPost, url, body)
    const resultParsed = JSON.parse(result.data)
    //console.log('Inside getUserTaxInfo:: ', resultParsed)
    yield put(getUserTaxInfoSuccess(resultParsed))
  } catch (e) {
    // console.warn(e)
  }
}

function* getCompanyUpdatesSaga() {
  try {
    const url = `${API_SERVER}/GetCompanyUpdates`
    const result = yield call(apiManualRequest, url)
    const resultParsed = JSON.parse(result.data)
    yield put(getCompanyUpdatesSuccess(resultParsed))
  } catch (e) {
    // console.warn(e)
  }
}

// Action to get the company update for specific user login
function* getUserCompanyUpdateInfoSaga() {
  try {
    const url = `${API_SERVER}/GetUserCompanyUpdateInfo`
    const uuid = yield select(getUuid)
    const body = JSON.stringify({
      uuid: uuid
    })
    const result = yield call(apiManualPost, url, body)
    const resultParsed = JSON.parse(result.data)
    yield put(getUserCompanyUpdateInfoSuccess(resultParsed))
  } catch (error) {
    yield put(getUserCompanyUpdateInfoFailed())
  }
}

function* setReadCompanyUpdateSaga({ data }) {
  try {
    const url = `${API_SERVER}/SetUserCompanyUpdateReadStatus`
    const uuid = yield select(getUuid)
    const id = data.map((item) => item.update_id)
    const body = JSON.stringify({
      uuid: uuid,
      companyUpdateIDs: id
    })
    const result = yield call(apiManualPost, url, body)
    if (
      result.data.message === 'Company updates are marked as read successfully.'
    ) {
      yield put(setReadCompanyUpdateSuccess(result.data))
    }
    yield put(getUserCompanyUpdateInfo())
  } catch (error) {
    yield put(setReadCompanyUpdateFailed(error))
  }
}

function* getRenewTokenSaga() {
  try {
    const url = `${KVT_IDENTITY_SERVER}/RenewTokens`
    const uuid = store.getState().client.user.data[2]
    const email = store.getState().client.user.data[1]
    const body = JSON.stringify({
      uuid: uuid,
      email: email
    })
    const result = yield call(registerPost, url, body)
    //console.log('Inside getRenewToken API:: ', result.data)
    yield put(setClient(result.data))
    sessionStorage.setItem('user', JSON.stringify(result.data))
  } catch (e) {
    // console.warn(e)
  }
}

function* getTokenValidationSaga() {
  try {
    const url = `${API_SERVER}/TokenValidation`
    const resultTokenValidate = yield call(apiSessionStorageRequest, url)
    //console.log('Inside getTokenValidation:: ',resultTokenValidate)
    if (resultTokenValidate.data === 'token validated scuccessfully') {
      const token = JSON.parse(sessionStorage.getItem('user'))
      //console.log('Inside getTokenValidation:: ', token)
      yield put(setClient(token))
    } else if (resultTokenValidate.data === 'Token expired') {
      yield put(onRenewToken())
      // console.log('Inside getTokenValidation:: ', resultRenewToken)
    }
  } catch (e) {
    // console.warn(e)
  }
}

// KAS-336 Referral code
function* sendReferralSaga({ data }) {
  try {
    const url = `${API_SERVER}/GetReferralUserDetails`
    const uuid = store.getState().client.user.data[2]
    const userType = store.getState().client.user.data[5]
    const { adminUser } = store.getState()
    const { selectedUser } = adminUser

    const body = JSON.stringify({
      uuid: userType == 2 ? selectedUser.uuid : uuid,
      lastname:
        userType == 2
          ? selectedUser.lastname
          : store.getState().client.user.data[4],
      firstname:
        userType == 2
          ? selectedUser.firstname
          : store.getState().client.user.data[3],
      email:
        userType == 2
          ? selectedUser.email
          : store.getState().client.user.data[1],
      recommendedpersonemail: data.email,
      referralpersonemail:
        userType == 2
          ? selectedUser.email
          : store.getState().client.user.data[1]
    })

    const result = yield call(apiManualPost, url, body)

    if (result.data === 'recommended person email not existed') {
      const addUrl = `${API_SERVER}/AddReferralUserDetails`
      const addResult = yield call(apiManualPost, addUrl, body)
      if (
        addResult.data ===
        'Email sent and referral user details are stored successfully'
      ) {
        yield put(sendReferralSuccess(addResult.data))
      }
    } else if (
      result.data ===
        'recommended person email existed or already register to the service' ||
      result.data === 'Eligible for referral program'
    ) {
      yield put(emailReferredOrRegistered())
    } else {
      yield put(sendReferralFailed(error))
    }
  } catch (error) {
    yield put(sendReferralFailed(error))
  }
}

// KAS-336 get referral discount by emai
function* getReferralDiscountByEmailSaga({ data }) {
  const selectedUser = data
  try {
    const url = `${API_SERVER}/GetReferralDiscountByEmail`
    const email = store.getState().client.user.data[1]
    const userType = yield select(getUserType)
    const body = JSON.stringify({
      email: userType == 2 ? selectedUser.email : email
    })
    const result = yield call(apiManualPost, url, body)

    yield put(getReferralDiscountByEmailSuccess(result.data))
  } catch (error) {
    yield put(getReferralDiscountByEmailFailed(error))
  }
}

function* getUserLifeTimeInfoSaga({ data }) {
  const selectedUser = data
  try {
    const url = `${API_SERVER}/GetUserLifeTimeAwardInfo`
    const uuid = yield select(getUuid)
    const userType = yield select(getUserType)
    const body = JSON.stringify({
      uuid: userType == 2 ? selectedUser.uuid : uuid
    })
    const result = yield call(apiManualPost, url, body)
    const resultParsed = JSON.parse(result.data)
    if (result.data) {
      yield put(getUserLifeTimeInfoSuccess(resultParsed[0]))
    }
  } catch (error) {
    yield put(getUserLifeTimeInfoFailed(error))
  }
}

function* updateCelebrationStatusSaga() {
  try {
    const url = `${API_SERVER}/UpdateCelebrationNotificationStatus`
    const uuid = yield select(getUuid)
    const body = JSON.stringify({
      uuid: uuid
    })
    const result = yield call(apiManualPost, url, body)
    if (
      result.data ===
      'User celebration notification status is updated successfully'
    ) {
      yield put(updateCelebrationStatusSuccess(result.data))
    }
  } catch (error) {
    yield put(updateCelebrationStatusFailed(error))
  }
}

export function* watchCheckAuthInfoSaga() {
  yield takeEvery(CHECK_AUTH_INFO, checkAuthInfoSaga)
}

export function* watchCustomersChartSaga() {
  yield takeEvery(GET_CUSTOMERS_CHART, getCustomersChartSaga)
}

export function* watchInvoiceChartSaga() {
  yield takeEvery(GET_INVOICE_CHART, getInvoiceChartSaga)
}

export function* watchGetUserTaxInfoSaga() {
  yield takeEvery(GET_USER_TAX_INFO, getUserTaxInfoSaga)
}

export function* watchGetCompanyUpdatesSaga() {
  yield takeEvery(GET_COMPANY_UPDATES, getCompanyUpdatesSaga)
}

export function* watchGetRenewTokenSaga() {
  yield takeEvery(GET_RENEW_TOKEN, getRenewTokenSaga)
}

export function* watchTokenValidationSaga() {
  yield takeEvery(TOKEN_VALIDATION, getTokenValidationSaga)
}

export function* watchSendReferralSaga() {
  yield takeEvery(SEND_REFERRAL, sendReferralSaga)
}
export function* watchGetReferralDiscountByEmailSaga() {
  yield takeEvery(
    GET_REFERRAL_DISCOUNT_BY_EMAIL,
    getReferralDiscountByEmailSaga
  )
}
export function* watchSetReadCompanyUpdateSaga() {
  yield takeEvery(SET_READ_COMPANY_UPDATE, setReadCompanyUpdateSaga)
}
export function* watchGetUserCompanyUpdateInfoSaga() {
  yield takeEvery(GET_USER_COMPANY_UPDATE_INFO, getUserCompanyUpdateInfoSaga)
}
export function* watchGetUserLifeTimeInfoSaga() {
  yield takeEvery(GET_USER_LIFETIME_INFO, getUserLifeTimeInfoSaga)
}
export function* watchUpdateCelebrationStatusSaga() {
  yield takeEvery(UPDATE_CELEBRATE_STATUS, updateCelebrationStatusSaga)
}
export function* watchGetSalaryDetailMonthlySaga() {
  yield takeEvery(
    GET_INVOICE_AND_SALARY_DETAIL_MONTHLY,
    getInvoiceAndSalaryDetailMonthlySaga
  )
}
